import type {
  ConsumptionCondition,
  EmittableDayConsumptionCondition,
  WireTimeOfDay,
} from '@/alerts/treeTypes'

type Option = { id: string; label: string }

type ClockTime = string & { readonly __brand: 'ClockTime' }

type TimeWindow = { start: ClockTime; end: ClockTime }

export type WindowProblem =
  | 'missing-start'
  | 'missing-end'
  | 'empty'
  | 'crosses-midnight'

const KINDS = [
  { id: '1', label: 'צ.יומית', consumption_type: 'meter', range: 'full' },
  { id: '2', label: 'צ.לילה', consumption_type: 'meter', range: 'night' },
  { id: '3', label: 'צ. משותפת', consumption_type: 'shared', range: 'full' },
  {
    id: '4',
    label: 'צ. בחלון שעות',
    consumption_type: 'meter',
    range: 'window',
  },
] as const

type Kind = (typeof KINDS)[number]

const WINDOW_KIND_ID = '4'

// The hour check is the hour period, so the menu lists it as a kind and
// picking it sets the period. The editor no longer starts a time-window
// choice. The engine still reads one, so a node that already holds it keeps
// its option and its value.
export const HOUR_KIND_ID = 'hour'

export function consumptionKindOptions(current: unknown): Option[] {
  return [
    ...KINDS.filter(
      k => k.id !== WINDOW_KIND_ID || String(current ?? '') === WINDOW_KIND_ID
    ).map(({ id, label }) => ({ id, label })),
    { id: HOUR_KIND_ID, label: 'צ.שעתית' },
  ]
}

function kindOf(id: unknown): Kind {
  return KINDS.find(k => k.id === String(id ?? '1')) ?? KINDS[0]
}

export function isWindowKind(id: unknown): boolean {
  return kindOf(id).range === 'window'
}

const HOURS = Array.from(
  { length: 24 },
  (_, h) => `${String(h).padStart(2, '0')}:00`
)
const START_HOURS = HOURS.slice(0, -1)
const END_HOURS = HOURS.slice(1)

export function windowHourOptions(
  edge: 'start' | 'end',
  current: unknown
): Option[] {
  const grid = edge === 'start' ? START_HOURS : END_HOURS
  const saved = parseClockTime(current)
  const times = saved && !grid.includes(saved) ? [...grid, saved].sort() : grid
  return times.map(t => ({ id: t, label: t }))
}

export function consumptionKindToCondition(
  data: Record<string, unknown>,
  nodeId: string
): Pick<EmittableDayConsumptionCondition, 'consumption_type' | 'time_range'> {
  const kind = kindOf(data.firstSelectedId)
  if (kind.range !== 'window') {
    return { consumption_type: kind.consumption_type, time_range: kind.range }
  }
  const parsed = parseWindow(data.windowStart, data.windowEnd)
  if (typeof parsed === 'string') {
    throw new Error(
      `consumptionKindToCondition: ${parsed} time window on ${nodeId}`
    )
  }
  return {
    consumption_type: kind.consumption_type,
    time_range: {
      start: toWireTime(parsed.start),
      end: toWireTime(parsed.end),
    },
  }
}

export function consumptionKindFromCondition(c: ConsumptionCondition): {
  firstSelectedId: string
  windowStart?: string
  windowEnd?: string
} {
  const timeRange = c.time_range ?? 'full'
  const range = typeof timeRange === 'string' ? timeRange : 'window'
  const ofType = (r: string) =>
    KINDS.find(k => k.consumption_type === c.consumption_type && k.range === r)
  const kind = ofType(range) ?? ofType('full') ?? KINDS[0]
  if (kind.range !== 'window' || typeof timeRange === 'string') {
    return { firstSelectedId: kind.id }
  }
  return {
    firstSelectedId: kind.id,
    windowStart: fromWireTime(timeRange.start) ?? '',
    windowEnd: fromWireTime(timeRange.end) ?? '',
  }
}

export function consumptionWindowProblem(
  data: Record<string, unknown>
): WindowProblem | null {
  if (!isWindowKind(data.firstSelectedId)) return null
  const parsed = parseWindow(data.windowStart, data.windowEnd)
  return typeof parsed === 'string' ? parsed : null
}

const CLOCK_TIME = /^([01]\d|2[0-3]):[0-5]\d$/

function parseClockTime(value: unknown): ClockTime | null {
  return typeof value === 'string' && CLOCK_TIME.test(value)
    ? (value as ClockTime)
    : null
}

// The engine reads [day + start, day + end) on one calendar day, so a window
// whose end is not after its start never fires.
function parseWindow(start: unknown, end: unknown): TimeWindow | WindowProblem {
  const s = parseClockTime(start)
  if (!s) return 'missing-start'
  const e = parseClockTime(end)
  if (!e) return 'missing-end'
  if (e === s) return 'empty'
  if (e < s) return 'crosses-midnight'
  return { start: s, end: e }
}

// `format: time` requires an RFC 3339 offset, but the engine casts with ::time,
// drops the zone, and reads Asia/Jerusalem wall-clock time. The Z satisfies the
// schema. It does not mean UTC.
function toWireTime(t: ClockTime): WireTimeOfDay {
  return `${t}:00Z` as WireTimeOfDay
}

const WIRE_TIME =
  /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d+)?)?(?:[zZ]|[+-]\d{2}:?\d{2})?$/

function fromWireTime(value: unknown): ClockTime | null {
  return typeof value === 'string' && WIRE_TIME.test(value)
    ? parseClockTime(value.slice(0, 5))
    : null
}
